<template>
  <v-app class="print-preview-app">
    <!-- Password Dialog for Shared Links -->
    <PasswordDialog
      v-model="showPasswordDialog"
      :error="passwordError"
      :verifying="verifyingPassword"
      @verify="verifyPasswordAndLoad"
      @cancel="window.close()"
    />

    <!-- Packlist Selector for Multi-List Shares -->
    <PacklistSelector
      v-model:show="showPacklistSelector"
      v-model:selectedIndex="selectedPacklistIndex"
      :packlists="sharedPacklists"
      @select="onPacklistSelected"
    />

    <!-- Multi-List Indicator -->
    <v-chip
      v-if="shared && sharedPacklists.length > 1 && !showPacklistSelector"
      class="multi-list-indicator screen-only"
      size="small"
      color="primary"
      variant="elevated"
      @click="openPacklistSelector"
    >
      <v-icon left size="small">mdi-format-list-bulleted-square</v-icon>
      {{ selectedPacklistIndex + 1 }} of {{ sharedPacklists.length }} lists
    </v-chip>

    <!-- Shared Link Breadcrumb -->
    <div v-if="shared && sharedPacklists.length > 1" class="shared-breadcrumb screen-only">
      <v-breadcrumbs
        :items="sharedBreadcrumbItems"
        class="px-4 py-2"
        density="compact"
      >
        <template v-slot:item="{ item }">
          <v-breadcrumbs-item
            v-if="!item.selector"
            :disabled="item.disabled"
            @click="item.click"
            class="text-body-2"
          >
            {{ item.title }}
          </v-breadcrumbs-item>
          
          <!-- List selector breadcrumb item -->
          <v-menu v-else bottom offset-y>
            <template v-slot:activator="{ props }">
              <v-breadcrumbs-item
                v-bind="props"
                :disabled="item.disabled"
                class="text-body-2 cursor-pointer"
                style="cursor: pointer;"
              >
                {{ item.title }}
                <v-icon size="small" class="ml-1">mdi-chevron-down</v-icon>
              </v-breadcrumbs-item>
            </template>
            
            <v-card max-width="400" elevation="8">
              <v-card-title class="text-subtitle-1 py-2">
                <v-icon class="mr-2">mdi-format-list-bulleted</v-icon>
                Switch to List
              </v-card-title>
              <v-divider></v-divider>
              <v-list density="compact" max-height="300" class="overflow-y-auto">
                <v-list-item
                  v-for="(list, index) in sharedPacklists"
                  :key="list.id"
                  @click="setCurrentSharedPacklist(index)"
                  :class="{ 'v-list-item--active': index === selectedPacklistIndex }"
                >
                  <v-list-item-title class="text-body-2">
                    {{ getPacklistDisplayName(list) }}
                  </v-list-item-title>
                  <v-list-item-subtitle v-if="list.description" class="text-caption">
                    {{ list.description }}
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-card>
          </v-menu>
        </template>
        
        <template v-slot:divider>
          <v-icon size="small">mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>
    </div>

    <!-- PDF Preview Dialog -->
    <PDFPreviewDialog
      v-model="showPDFPreview"
      :pdf-url="previewPDFUrl"
      @download="downloadPDF"
      @print="printPDF"
      @close="closePDFPreview"
      @error="handlePDFError"
    />

    <!-- Loading State -->
    <div v-if="loading || showPasswordDialog" class="loading-container">
      <v-progress-circular v-if="loading" indeterminate color="primary" size="64"></v-progress-circular>
      <p v-if="loading" class="mt-4">Loading shared list...</p>
    </div>

    <!-- Print Preview Interface -->
    <div v-else class="print-preview-container">
      <!-- Docked Controls Panel -->
      <PrintControls
        v-if="controlsExpanded"
        v-model:expanded="controlsExpanded"
        v-model:paper-format="paperFormat"
        v-model:column-count="columnCount"
        v-model:show-images="showImages"
        v-model:show-title="showTitle"
        v-model:show-description="showDescription"
        v-model:show-checked-items="showCheckedItems"
        v-model:show-metadata="showMetadata"
        v-model:show-header-image="showHeaderImage"
        v-model:header-image-opacity="headerImageOpacity"
        :paper-formats="paperFormats"
        :has-header-image="!!currentList?.header_image_url"
        :exporting-p-d-f="exportingPDF"
        @export-p-d-f="exportToPDF"
      />

      <!-- Preview Area -->
      <div class="preview-area">
        <!-- Paper Container -->
        <div 
          class="paper-container" 
          :class="`paper-${paperFormat.toLowerCase()}`"
          :style="currentList?.header_image_url && showHeaderImage ? headerImageContainerStyle : {}"
        >
          <div 
            class="paper-page" 
            :class="{ 'images-disabled': !showImages }"
          >
            <!-- Header Image for Print (mobile compatibility) -->
            <img 
              v-if="currentList?.header_image_url && showHeaderImage"
              :src="getHeaderImageUrl()"
              class="header-image-print"
              alt="Header background"
              :style="{ opacity: headerImageOpacity / 100 }"
            />
            
            <!-- Print Header -->
            <PrintHeader
              :show-title="showTitle"
              :show-description="showDescription"
              :show-metadata="showMetadata"
              :title="getPacklistDisplayName(currentList) || 'Packlist'"
              :description="currentList?.description || ''"
              :duration="currentList?.duration || 1"
              :total-items="totalItems"
              :checked-items="checkedItems"
              :formatted-date="formatDate(new Date())"
              :show-edit-button="!shared && currentList"
              :show-list-selector-button="shared && sharedPacklists.length > 1"
              @edit="openEditPacklistDialog"
              @switch-list="openPacklistSelector"
            />

            <!-- Print Content -->
            <div class="print-content" :class="[`columns-${columnCount}`, { 'show-images': showImages }]" style="position: relative; z-index: 1;">
              <GroupSection
                v-for="(group, index) in groupedItems" 
                :key="group.name || 'general'"
                :group="group"
                :group-data="getGroupData(group.name)"
                :show-images="showImages"
                :shared="shared"
                :can-edit-groups="canEditGroups"
                :can-delete-groups="canDeleteGroups"
                :can-reorder-groups="canReorderGroups"
                :is-first="index === 0"
                :is-last="index === groupedItems.length - 1"
                :can-add-items="canAddItems"
                :can-edit-items="canEditItems"
                :can-delete-items="canDeleteItems"
                :can-reorder-items="canReorderItems"
                :group-image-style="getGroupImageStyle(getGroupData(group.name))"
                :get-calculated-quantity="getCalculatedQuantity"
                v-model:quick-add-value="quickAddItems[group.name || 'General']"
                :quick-add-loading="quickAddLoading[group.name || 'General'] || false"
                @edit-group="editGroup"
                @delete-group="deleteGroup"
                @move-group-up="moveGroupUp"
                @move-group-down="moveGroupDown"
                @item-reorder="onItemReorder"
                @update-item-check="updateItemCheck"
                @edit-item="editItem"
                @delete-item="confirmDeleteItem"
                @quick-add="quickAddItem"
                @quick-add-and-stay="quickAddItemAndStay"
                @retry-item="retryOptimisticItem"
                @remove-item="removeOptimisticItem"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Packlist Dialog -->
    <ListDialog
      v-model="showEditPacklistDialog"
      :initial-data="currentList"
      @updated="handlePacklistUpdated"
      @message="handleMessage"
    />

    <!-- Edit Group Dialog -->
    <EditGroupDialog
      v-model="showEditGroupDialog"
      :group-data="selectedGroup"
      @updated="handleGroupUpdated"
      @message="handleMessage"
    />

    <!-- Delete Group Dialog -->
    <DeleteGroupDialog
      v-model="showDeleteGroupDialog"
      :group-data="groupToDelete"
      @deleted="handleGroupDeleted"
      @message="handleMessage"
    />

    <!-- Edit Item Dialog -->
    <EditItemDialog
      v-model="showEditItemDialog"
      :item-data="selectedItem"
      :shared="shared"
      :link-id="link_id"
      :hmac="hmac"
      :authenticated-password="authenticatedPassword"
      :group-options="groupOptions"
      @updated="handleItemUpdated"
      @message="handleMessage"
    />

    <!-- Delete Item Confirmation Dialog -->
    <DeleteItemDialog
      v-model="showDeleteItemDialog"
      :item="itemToDelete"
      :deleting="deletingItem"
      @delete="deleteItem"
      @cancel="cancelDeleteItem"
    />

    <!-- Delete Selected Items Dialog -->
    <DeleteSelectedItemsDialog
      v-model="showDeleteSelectedDialog"
      :count="selectedItems.length"
      :deleting="deletingSelectedItems"
      @confirm="confirmDeleteSelectedItems"
      @cancel="showDeleteSelectedDialog = false"
    />

    <!-- Unselect Selected Items Dialog -->
    <UnselectSelectedItemsDialog
      v-model="showUnselectSelectedDialog"
      :count="selectedItems.length"
      :processing="unselectingSelectedItems"
      @confirm="confirmUnselectSelectedItems"
      @cancel="showUnselectSelectedDialog = false"
    />

    <!-- Add Item Dialog -->
    <AddItemDialog
      v-model="showAddItemDialog"
      :editing-item="editingItemForAdd"
      :group-options="groupOptionsForAddDialog"
      @saved="handleItemSaved"
      @message="handleMessage"
    />

    <!-- Add Group Dialog -->
    <AddGroupDialog
      v-model="showAddGroupDialog"
      @created="handleGroupCreated"
      @message="handleMessage"
    />

    <!-- Floating Dock -->
    <FloatingDock
      v-if="!loading && currentList"
      :can-add-groups="canAddGroups"
      :can-add-items="canAddItems"
      @add-group="openAddGroupDialog"
      @add-item="openAddItemDialog"
      @toggle-print-settings="togglePrintSettings"
      @delete-selected="openDeleteSelectedDialog"
      @unselect-selected="showUnselectSelectedDialog = true"
      :can-delete-items="canDeleteItems"
      :has-selected-items="selectedItems.length > 0"
    />

    <!-- Snackbar for messages -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-app>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useListStore } from '../stores/list'
import { useGroupStore } from '../stores/group'
import { useAuthStore } from '../stores/auth'
import { usePrintSettings } from '../composables/usePrintSettings'
import { useImageAuthentication } from '../composables/useImageAuthentication'
import { usePDFGeneration } from '../composables/usePDFGeneration'
import axios from 'axios'
import ListDialog from '../components/ListDialog.vue'
import EditGroupDialog from '../components/EditGroupDialog.vue'
import DeleteGroupDialog from '../components/DeleteGroupDialog.vue'
import EditItemDialog from '../components/EditItemDialog.vue'
import AddItemDialog from '../components/AddItemDialog.vue'
import AddGroupDialog from '../components/AddGroupDialog.vue'
import PasswordDialog from '../components/print/PasswordDialog.vue'
import PDFPreviewDialog from '../components/print/PDFPreviewDialog.vue'
import DeleteItemDialog from '../components/print/DeleteItemDialog.vue'
import DeleteSelectedItemsDialog from '../components/print/DeleteSelectedItemsDialog.vue'
import UnselectSelectedItemsDialog from '../components/print/UnselectSelectedItemsDialog.vue'
import PrintControls from '../components/print/PrintControls.vue'
import FloatingDock from '../components/print/FloatingDock.vue'
import QuickAddItem from '../components/print/QuickAddItem.vue'
import GroupHeader from '../components/print/GroupHeader.vue'
import ItemRow from '../components/print/ItemRow.vue'
import PrintHeader from '../components/print/PrintHeader.vue'
import GroupSection from '../components/print/GroupSection.vue'
import PacklistSelector from '../components/print/PacklistSelector.vue'
import draggable from 'vuedraggable'

export default {
  name: 'ListDetailView',
  components: {
    ListDialog,
    EditGroupDialog,
    DeleteGroupDialog,
    EditItemDialog,
    AddItemDialog,
    AddGroupDialog,
    PasswordDialog,
    PDFPreviewDialog,
    DeleteItemDialog,
    DeleteSelectedItemsDialog,
    UnselectSelectedItemsDialog,
    PrintControls,
    FloatingDock,
    QuickAddItem,
    GroupHeader,
    ItemRow,
    PrintHeader,
    GroupSection,
    PacklistSelector,
    draggable
  },
  props: {
    shared: {
      type: Boolean,
      default: false
    },
    link_id: {
      type: String,
      default: null
    },
    hmac: {
      type: String,
      default: null
    },
    listId: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const route = useRoute()
    const listStore = useListStore()
    const groupStore = useGroupStore()
    const authStore = useAuthStore()
    
    // Use composables
    const printSettings = usePrintSettings()
    const {
      paperFormat,
      paperFormats,
      columnCount,
      controlsExpanded,
      showImages,
      showTitle,
      showDescription,
      showCheckedItems,
      showMetadata,
      showHeaderImage,
      headerImageOpacity,
      exportingPDF
    } = printSettings
    
    // Shared data state
    const sharedPacklist = ref(null)
    const sharedPacklists = ref([])
    const selectedPacklistIndex = ref(0)
    const showPacklistSelector = ref(false)
    const sharedGroups = ref([])
    const sharedPermissions = ref({})
    const loading = ref(false)
    
    // Password dialog state
    const showPasswordDialog = ref(false)
    const passwordError = ref('')
    const verifyingPassword = ref(false)
    const authenticatedPassword = ref(null) // Store the verified password for image URLs
    
    // Use PDF generation composable
    const {
      showPDFPreview,
      previewPDFUrl,
      exportToPDF: exportToPDFComposable,
      downloadPDF,
      printPDF,
      closePDFPreview,
      handlePDFError
    } = usePDFGeneration()
    
    // Use image authentication composable
    const { getAuthenticatedImageUrl, getGroupImageStyle } = useImageAuthentication(
      computed(() => props.shared),
      computed(() => props.link_id),
      computed(() => props.hmac),
      authenticatedPassword
    )
    
    // Edit list dialog state
    const showEditPacklistDialog = ref(false)
    
    // Edit group dialog state
    const showEditGroupDialog = ref(false)
    const selectedGroup = ref(null)
    
    // Delete group dialog state
    const showDeleteGroupDialog = ref(false)
    const groupToDelete = ref(null)
    
    // Edit item dialog state
    const showEditItemDialog = ref(false)
    const selectedItem = ref(null)
    
    // Delete item dialog state
    const showDeleteItemDialog = ref(false)
    const itemToDelete = ref(null)
    const deletingItem = ref(false)

    // Delete selected items dialog state
    const showDeleteSelectedDialog = ref(false)
    const deletingSelectedItems = ref(false)

    // Unselect selected items dialog state
    const showUnselectSelectedDialog = ref(false)
    const unselectingSelectedItems = ref(false)

    // Add item dialog state
    const showAddItemDialog = ref(false)
    const editingItemForAdd = ref(null)

    // Add group dialog state
    const showAddGroupDialog = ref(false)
    
    // Quick add item state
    const quickAddItems = ref({})
    const quickAddLoading = ref({})
    
    // Snackbar state
    const snackbar = ref(false)
    const snackbarText = ref('')
    const snackbarColor = ref('success')


    const currentList = computed(() => {
      return props.shared ? sharedPacklist.value : listStore.currentList
    })

    const groupedItems = computed(() => {
      if (!currentList.value?.items) return []
      
      const groups = {}
      // Create a map of group name to order index from the authoritative source
      const authoritativeGroupOrder = {}
      const sourceGroups = props.shared ? sharedGroups.value : groupStore.groups
      
      if (sourceGroups) {
        sourceGroups.forEach((g, index) => {
          if (g && g.name) {
            // Use the array index as the sort order since the API returns a sorted list
            authoritativeGroupOrder[g.name] = index
          }
        })
      }
      
      currentList.value.items
        .filter(item => showCheckedItems.value || !item.is_checked) // Filter based on show/hide toggle
        .sort((a, b) => a.order_index - b.order_index)
        .forEach(item => {
          const groupName = item.group?.name || ''
          if (!groups[groupName]) {
            groups[groupName] = []
          }
          groups[groupName].push(item)
        })
      
      return Object.entries(groups)
        .map(([name, items]) => {
          // Use authoritative order if available, otherwise fall back to explicit index if present, else end
          let order = 999999
          if (name !== '') {
            if (authoritativeGroupOrder[name] !== undefined) {
              order = authoritativeGroupOrder[name]
            } else if (items[0]?.group?.order_index !== undefined) {
              order = items[0].group.order_index
            }
          }
          
          return {
            name: name === '' ? null : name,
            items,
            groupOrder: order
          }
        })
        .sort((a, b) => a.groupOrder - b.groupOrder)
        .map(({ name, items }) => ({ name, items }))
    })

    const totalItems = computed(() => {
      return currentList.value?.items?.length || 0
    })

    const sharedBreadcrumbItems = computed(() => {
      if (!props.shared || sharedPacklists.value.length <= 1) return []
      
      const items = []
      
      // Add share name as root (fallback to "Shared Lists" if not available)
      const shareName = sharedPermissions.value?.name || 'Shared Lists'
      items.push({
        title: shareName,
        disabled: true
      })
      
      // Add current list with selector
      if (currentList.value) {
        items.push({
          title: getPacklistDisplayName(currentList.value),
          selector: true,
          disabled: false
        })
      }
      
      return items
    })

    const checkedItems = computed(() => {
      return currentList.value?.items?.filter(item => item.is_checked).length || 0
    })

    const selectedItems = computed(() => {
      return currentList.value?.items?.filter(item => item.is_checked) || []
    })

    // Create group options for the EditItemDialog
    const groupOptions = computed(() => {
      if (props.shared) {
        // For shared links, use the dedicated groups data from the API response
        return sharedGroups.value.map(group => ({ id: group.id, name: group.name }))
      } else {
        // For authenticated users, use groupStore
        return (groupStore.groups || []).map(group => ({ id: group.id, name: group.name }))
      }
    })

    // Permissions for floating dock
    const canAddGroups = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_add_groups || false
      }
      return true // For authenticated users, always allow
    })

    const canAddItems = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_create_items || false
      }
      return true // For authenticated users, always allow
    })

    const canEditGroups = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_update_groups || false
      }
      return true // For authenticated users, always allow
    })

    const canDeleteGroups = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_delete_groups || false
      }
      return true // For authenticated users, always allow
    })

    const canEditItems = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_edit_items || false
      }
      return true // For authenticated users, always allow
    })

    const canDeleteItems = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_delete_items || false
      }
      return true // For authenticated users, always allow
    })

    const canReorderItems = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_update_groups || false
      }
      return true // For authenticated users, always allow
    })

    const canReorderGroups = computed(() => {
      if (props.shared) {
        return sharedPermissions.value?.can_update_groups || false
      }
      return true // For authenticated users, always allow
    })

    // Group options for the add item dialog (duplicate computed - should be consolidated)
    const groupOptionsForAddDialog = computed(() => {
      if (props.shared) {
        // For shared links, use the dedicated groups data from the API response
        return sharedGroups.value || []
      }
      return groupStore.groups || []
    })

    const getHeaderImageUrl = () => {
      return getAuthenticatedImageUrl(currentList.value?.header_image_url)
    }

    const headerImageStyle = computed(() => {
      if (currentList.value?.header_image_url && showHeaderImage.value) {
        const imageUrl = getHeaderImageUrl()
        if (imageUrl) {
          return {
            position: 'relative'
          }
        }
      }
      return {}
    })

    const headerImageBackgroundStyle = computed(() => {
      if (currentList.value?.header_image_url && showHeaderImage.value) {
        const imageUrl = getHeaderImageUrl()
        if (imageUrl) {
          return {
            position: 'absolute',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundImage: `url("${imageUrl}")`,
            backgroundSize: '100%',
            backgroundPosition: 'center top',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'local',
            opacity: headerImageOpacity.value / 100,
            zIndex: '0',
            pointerEvents: 'none'
          }
        }
      }
      return {}
    })

    const headerImageContainerStyle = computed(() => {
      if (currentList.value?.header_image_url && showHeaderImage.value) {
        const imageUrl = getHeaderImageUrl()
        if (imageUrl) {
          return {
            backgroundImage: `url("${imageUrl}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'local',
            minHeight: 'calc(297mm * 0.7)', // A4 height ratio for proper aspect
            position: 'relative',
            '--header-image-opacity': headerImageOpacity.value / 100
          }
        }
      }
      return {}
    })


    const getCalculatedQuantity = (item) => {
      if (!currentList.value) return item.quantity
      
      if (item.quantity_type === 'per_days' && item.per_days) {
        const totalDays = currentList.value.duration
        return Math.ceil(totalDays / item.per_days) * item.quantity
      }
      return item.quantity
    }
    
    const formatDuration = (days) => {
      if (!days || days === 1) return ''
      
      if (days === 7) return ' (1 week)'
      if (days === 14) return ' (2 weeks)'
      if (days === 21) return ' (3 weeks)'
      if (days === 28) return ' (4 weeks)'
      
      // For weeks + days
      if (days > 7 && days % 7 === 0) {
        const weeks = Math.floor(days / 7)
        return ` (${weeks} weeks)`
      }
      
      // For mixed weeks and days
      if (days > 7) {
        const weeks = Math.floor(days / 7)
        const remainingDays = days % 7
        if (remainingDays === 0) {
          return ` (${weeks} week${weeks !== 1 ? 's' : ''})`
        }
        return ` (${weeks} week${weeks !== 1 ? 's' : ''} and ${remainingDays} day${remainingDays !== 1 ? 's' : ''})`
      }
      
      // Just days
      return ` (${days} days)`
    }
    
    const getPacklistDisplayName = (list) => {
      if (!list) return ''
      return list.name
    }

    const formatDate = (date) => {
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    }

    const getGroupData = (groupName) => {
      if (props.shared && currentList.value?.items) {
        // For shared data, extract group info from items
        const item = currentList.value.items.find(item => item.group?.name === groupName)
        return item?.group
      }
      return groupStore.groups.find(g => g.name === groupName)
    }

    const getGroupImageUrl = (groupData) => {
      return getAuthenticatedImageUrl(groupData?.image_url)
    }


    const exportToPDF = async () => {
      try {
        exportingPDF.value = true
        
        await exportToPDFComposable({
          paperFormat: paperFormat.value,
          columnCount: columnCount.value,
          showTitle: showTitle.value,
          showDescription: showDescription.value,
          showMetadata: showMetadata.value,
          showHeaderImage: showHeaderImage.value,
          headerImageOpacity: headerImageOpacity.value,
          showImages: showImages.value,
          currentList: currentList.value,
          groupedItems: groupedItems.value,
          totalItems: totalItems.value,
          checkedItems: checkedItems.value,
          getHeaderImageUrl,
          getPacklistDisplayName,
          formatDate,
          getGroupData,
          getGroupImageUrl,
          getCalculatedQuantity
        })
      } catch (error) {
        console.error('Error generating PDF:', error)
        alert('Failed to generate PDF. Please try again.')
      } finally {
        exportingPDF.value = false
      }
    }

    const loadSharedData = async (password = null) => {
      if (!props.shared || !props.link_id) return
      
      // If HMAC is missing, try to get it from the current URL
      let hmacToUse = props.hmac
      if (!hmacToUse) {
        const urlParams = new URLSearchParams(window.location.search)
        hmacToUse = urlParams.get('hmac')
      }
      
      if (!hmacToUse) {
        // Show error message if no HMAC found
        const errorMessage = 'Invalid shared link. Please use the complete link provided to you.'
        snackbarText.value = errorMessage
        snackbarColor.value = 'error'
        snackbar.value = true
        loading.value = false
        return
      }
      
      try {
        loading.value = true
        passwordError.value = ''
        
        // Build URL using new public access endpoint
        const params = new URLSearchParams({
          hmac: hmacToUse
        })
        if (password) {
          params.append('password', password)
        }
        
        const response = await axios.get(`/api/public/${props.link_id}?${params}`)
        
        // If password is required but we don't have one, show password dialog
        if (response.data.requires_password && !password) {
          showPasswordDialog.value = true
          loading.value = false
          return
        }
        
        // Handle multi-list shared data
        const packlists = response.data.packlists || []
        if (packlists.length > 0) {
          sharedPacklists.value = packlists
          
          // Find the most recently updated list (by checking items' created_at dates)
          let mostRecentIndex = 0
          let mostRecentDate = null
          
          packlists.forEach((list, index) => {
            if (list.items && list.items.length > 0) {
              // Find the most recent item in this list
              const listMostRecent = list.items.reduce((latest, item) => {
                const itemDate = new Date(item.created_at)
                return !latest || itemDate > new Date(latest.created_at) ? item : latest
              }, null)
              
              if (listMostRecent) {
                const listDate = new Date(listMostRecent.created_at)
                if (!mostRecentDate || listDate > mostRecentDate) {
                  mostRecentDate = listDate
                  mostRecentIndex = index
                }
              }
            }
          })
          
          // Set the most recently updated list (or first if no items found)
          setCurrentSharedPacklist(mostRecentIndex)
          selectedPacklistIndex.value = mostRecentIndex
        } else {
          // No packlists found
          throw new Error('No lists found in this shared link')
        }
        
        sharedPermissions.value = response.data.shared_link || {}
        authenticatedPassword.value = password // Store the password for image URLs
        
        // Store shared link name in the store for use in App header
        if (response.data.shared_link?.name) {
          listStore.setSharedLinkName(response.data.shared_link.name)
        }
        
        showPasswordDialog.value = false
        
      } catch (error) {
        let errorMessage = 'Failed to load shared content'
        
        if (error.response?.status === 401 || error.response?.status === 403) {
          if (error.response?.data?.requires_password && !password) {
            showPasswordDialog.value = true
            return
          } else if (password) {
            passwordError.value = 'Invalid password or access denied'
            return
          } else {
            errorMessage = 'Access denied to this shared link'
          }
        } else if (error.response?.status === 404) {
          errorMessage = 'Shared link not found or has expired'
        } else if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail
        } else if (error.message) {
          errorMessage = `Network error: ${error.message}`
        }
        
        // Show error to user
        snackbarText.value = errorMessage
        snackbarColor.value = 'error'
        snackbar.value = true
      } finally {
        if (!showPasswordDialog.value) {
          loading.value = false
        }
      }
    }

    const verifyPasswordAndLoad = async (password) => {
      if (!password?.trim()) {
        passwordError.value = 'Password is required'
        return
      }
      
      try {
        verifyingPassword.value = true
        passwordError.value = ''
        await loadSharedData(password)
      } finally {
        verifyingPassword.value = false
      }
    }

    const setCurrentSharedPacklist = (index) => {
      if (!sharedPacklists.value[index]) return
      
      const selectedList = sharedPacklists.value[index]
      sharedPacklist.value = selectedList
      selectedPacklistIndex.value = index
      
      // Get groups only from the currently selected list
      const currentListGroups = []
      if (selectedList.items) {
        selectedList.items.forEach(item => {
          if (item.group && !currentListGroups.find(g => g.id === item.group.id)) {
            currentListGroups.push(item.group)
          }
        })
      }
      sharedGroups.value = currentListGroups
    }

    const onPacklistSelected = (index) => {
      setCurrentSharedPacklist(index)
      showPacklistSelector.value = false
    }

    const openPacklistSelector = () => {
      if (sharedPacklists.value.length > 1) {
        showPacklistSelector.value = true
      }
    }

    const editGroup = (groupData) => {
      if (!groupData) return
      selectedGroup.value = groupData
      showEditGroupDialog.value = true
    }

    const deleteGroup = (groupData) => {
      if (!groupData) return
      groupToDelete.value = groupData
      showDeleteGroupDialog.value = true
    }

    const moveGroupUp = async (groupData) => {
      if (!groupData || !currentList.value) return
      
      try {
        // Get all groups in the current list and their current order
        const allGroups = props.shared ? sharedGroups.value : groupStore.groups
        const sortedGroups = [...allGroups].sort((a, b) => a.order_index - b.order_index)
        
        // Find the current group's position
        const currentIndex = sortedGroups.findIndex(g => g.id === groupData.id)
        if (currentIndex === -1 || currentIndex === 0) {
          return // Can't move up if it's the first group or not found
        }
        
        // Create new order by swapping with previous group
        const newOrder = [...sortedGroups]
        ;[newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]]
        const groupIds = newOrder.map(g => g.id)
        
        if (props.shared) {
          // Rearrange groups via shared link endpoint
          const params = new URLSearchParams({
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          await axios.put(`/api/public/${props.link_id}/packlists/${currentList.value.id}/groups/rearrange?${params}`, groupIds)
          
          // Reload shared data to reflect changes
          await loadSharedData(authenticatedPassword.value)
        } else {
          // Rearrange groups via regular API
          await groupStore.rearrangeGroups(currentList.value.id, groupIds)
          // Refresh the current list to show updated group order in items
          if (listStore.currentList) {
            await listStore.setCurrentList(listStore.currentList)
          }
        }
        
        handleMessage({ message: 'Group moved up successfully', color: 'success' })
      } catch (error) {
        console.error('Failed to move group up:', error)
        
        let errorMessage = 'Failed to move group up'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to reorder groups'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      }
    }

    const moveGroupDown = async (groupData) => {
      if (!groupData || !currentList.value) return
      
      try {
        // Get all groups in the current list and their current order
        const allGroups = props.shared ? sharedGroups.value : groupStore.groups
        const sortedGroups = [...allGroups].sort((a, b) => a.order_index - b.order_index)
        
        // Find the current group's position
        const currentIndex = sortedGroups.findIndex(g => g.id === groupData.id)
        if (currentIndex === -1 || currentIndex === sortedGroups.length - 1) {
          return // Can't move down if it's the last group or not found
        }
        
        // Create new order by swapping with next group
        const newOrder = [...sortedGroups]
        ;[newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]]
        const groupIds = newOrder.map(g => g.id)
        
        if (props.shared) {
          // Rearrange groups via shared link endpoint
          const params = new URLSearchParams({
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          await axios.put(`/api/public/${props.link_id}/packlists/${currentList.value.id}/groups/rearrange?${params}`, groupIds)
          
          // Reload shared data to reflect changes
          await loadSharedData(authenticatedPassword.value)
        } else {
          // Rearrange groups via regular API
          await groupStore.rearrangeGroups(currentList.value.id, groupIds)
          // Refresh the current list to show updated group order in items
          if (listStore.currentList) {
            await listStore.setCurrentList(listStore.currentList)
          }
        }
        
        handleMessage({ message: 'Group moved down successfully', color: 'success' })
      } catch (error) {
        console.error('Failed to move group down:', error)
        
        let errorMessage = 'Failed to move group down'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to reorder groups'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      }
    }

    const editItem = (item) => {
      if (!item) return
      selectedItem.value = item
      showEditItemDialog.value = true
    }

    const confirmDeleteItem = (item) => {
      if (!item) return
      itemToDelete.value = item
      showDeleteItemDialog.value = true
    }

    const cancelDeleteItem = () => {
      itemToDelete.value = null
      showDeleteItemDialog.value = false
    }

    const deleteItem = async () => {
      if (!itemToDelete.value) return
      
      try {
        deletingItem.value = true
        
        if (props.shared) {
          // Delete via consolidated endpoint with shared link parameters
          const params = new URLSearchParams({
            link_id: props.link_id,
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          await axios.delete(`/api/items/${itemToDelete.value.id}?${params}`)
          
          // Remove from shared data locally
          if (sharedPacklist.value?.items) {
            const itemIndex = sharedPacklist.value.items.findIndex(item => item.id === itemToDelete.value.id)
            if (itemIndex !== -1) {
              sharedPacklist.value.items.splice(itemIndex, 1)
            }
          }
        } else {
          // Delete via regular API
          await listStore.deleteItem(itemToDelete.value.id)
        }
        
        handleMessage({ message: 'Item deleted successfully', color: 'success' })
        cancelDeleteItem()
        
      } catch (error) {
        console.error('Failed to delete item:', error)
        
        let errorMessage = 'Failed to delete item'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to delete items'
        } else if (error.response?.status === 404) {
          errorMessage = 'Item not found'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      } finally {
        deletingItem.value = false
      }
    }

    const quickAddItem = async (groupName) => {
      const input = quickAddItems.value[groupName]?.trim()
      if (!input || !currentList.value) return
      
      // Set loading state
      quickAddLoading.value[groupName] = true
      
      try {
        // Parse quantity from input like "Socks (7x)" or "Socks (7)"
        let itemName = input
        let quantity = 1
        
        const quantityMatch = input.match(/^(.+?)\s*\((\d+)x?\)$/)
        if (quantityMatch) {
          itemName = quantityMatch[1].trim()
          quantity = parseInt(quantityMatch[2])
        }
        
        // Find group ID
        let groupId = null
        if (props.shared) {
          // For shared data, use the current list's groups only
          const group = sharedGroups.value.find(g => g.name === groupName)
          groupId = group?.id || null
        } else {
          const group = groupStore.groups.find(g => g.name === groupName)
          groupId = group?.id || null
        }
        
        const itemData = {
          name: itemName,
          description: '',
          quantity: quantity,
          quantity_type: 'fixed',
          per_days: null,
          group_id: groupId,
          packlist_id: currentList.value?.id
        }
        
        
        let result
        if (props.shared) {
          // Add via public shared link endpoint
          const params = new URLSearchParams({
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          const response = await axios.post(`/api/public/${props.link_id}/items?${params}`, itemData)
          
          
          // Add to shared data locally
          if (sharedPacklist.value?.items) {
            sharedPacklist.value.items.push(response.data)
            // Sort items by order_index to ensure proper positioning
            sharedPacklist.value.items.sort((a, b) => a.order_index - b.order_index)
          }
          result = { success: true }
        } else {
          // Add via regular API
          result = await listStore.addItem(currentList.value.id, itemData)
        }
        
        if (result.success) {
          // Clear the input field immediately on success
          quickAddItems.value[groupName] = ''
          const quantityText = quantity > 1 ? ` (${quantity}x)` : ''
          handleMessage({ message: `"${itemName}${quantityText}" added to ${groupName}!`, color: 'success' })
        } else {
          // Keep the input value on error
          handleMessage({ message: result.error, color: 'error' })
        }
      } catch (error) {
        console.error('Failed to add item:', error)
        
        let errorMessage = 'Failed to add item'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to add items'
        }
        
        // Keep the input value on error so user can retry
        handleMessage({ message: errorMessage, color: 'error' })
      } finally {
        // Clear loading state
        quickAddLoading.value[groupName] = false
      }
    }

    const quickAddItemAndStay = async (groupName, event) => {
      const input = quickAddItems.value[groupName]?.trim()
      if (!input || !currentList.value) return
      
      // Store reference to the current input element
      const currentInput = event.target
      
      // Prevent the default behavior that might cause focus to move
      event.preventDefault()
      
      // Parse quantity from input like "Socks (7x)" or "Socks (7)"
      let itemName = input
      let quantity = 1
      
      const quantityMatch = input.match(/^(.+?)\s*\((\d+)x?\)$/)
      if (quantityMatch) {
        itemName = quantityMatch[1].trim()
        quantity = parseInt(quantityMatch[2])
      }
      
      // Find group ID and group data
      let groupId = null
      let groupData = null
      if (props.shared) {
        groupData = sharedGroups.value.find(g => g.name === groupName)
        groupId = groupData?.id || null
      } else {
        groupData = groupStore.groups.find(g => g.name === groupName)
        groupId = groupData?.id || null
      }
      
      // Clear input immediately
      quickAddItems.value[groupName] = ''
      
      // Create optimistic item with temporary ID
      const tempId = `temp_${Date.now()}_${Math.random()}`
      const optimisticItem = {
        id: tempId,
        name: itemName,
        description: '',
        quantity: quantity,
        quantity_type: 'fixed',
        per_days: null,
        group_id: groupId,
        group: groupData,
        packlist_id: currentList.value?.id,
        is_checked: false,
        order_index: (currentList.value?.items?.length || 0),
        created_at: new Date().toISOString(),
        _isOptimistic: true,
        _isLoading: true,
        _hasError: false
      }
      
      // Add optimistic item to UI immediately
      if (props.shared && sharedPacklist.value?.items) {
        sharedPacklist.value.items.push(optimisticItem)
        sharedPacklist.value.items.sort((a, b) => a.order_index - b.order_index)
      } else if (!props.shared && listStore.currentList?.items) {
        listStore.currentList.items.push(optimisticItem)
      }
      
      // Refocus input immediately
      await nextTick()
      if (currentInput && document.contains(currentInput)) {
        currentInput.focus()
      }
      
      // Now make the API call
      try {
        const itemData = {
          name: itemName,
          description: '',
          quantity: quantity,
          quantity_type: 'fixed',
          per_days: null,
          group_id: groupId,
          packlist_id: currentList.value?.id
        }
        
        let response
        if (props.shared) {
          // Add via public shared link endpoint
          const params = new URLSearchParams({
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          response = await axios.post(`/api/public/${props.link_id}/items?${params}`, itemData)
          
          // Replace optimistic item with real item
          if (sharedPacklist.value?.items) {
            const index = sharedPacklist.value.items.findIndex(item => item.id === tempId)
            if (index !== -1) {
              sharedPacklist.value.items[index] = response.data
              sharedPacklist.value.items.sort((a, b) => a.order_index - b.order_index)
            }
          }
        } else {
          // Add via regular API
          const result = await listStore.addItem(currentList.value.id, itemData)
          
          if (result.success && listStore.currentList?.items) {
            // Remove optimistic item and let the store handle the real item
            const index = listStore.currentList.items.findIndex(item => item.id === tempId)
            if (index !== -1) {
              listStore.currentList.items.splice(index, 1)
            }
          }
        }
        
        const quantityText = quantity > 1 ? ` (${quantity}x)` : ''
        handleMessage({ message: `"${itemName}${quantityText}" added to ${groupName}!`, color: 'success' })
        
      } catch (error) {
        console.error('Failed to add item:', error)
        
        // Mark optimistic item as failed
        const items = props.shared ? sharedPacklist.value?.items : listStore.currentList?.items
        if (items) {
          const failedItem = items.find(item => item.id === tempId)
          if (failedItem) {
            failedItem._isLoading = false
            failedItem._hasError = true
            failedItem._retryData = { itemName, quantity, groupName, groupId, currentInput }
          }
        }
        
        let errorMessage = 'Failed to add item'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to add items'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      }
    }

    const retryOptimisticItem = async (item) => {
      if (!item._retryData) return
      
      const { itemName, quantity, groupName, groupId } = item._retryData
      
      // Reset item state to loading
      item._isLoading = true
      item._hasError = false
      
      try {
        const itemData = {
          name: itemName,
          description: '',
          quantity: quantity,
          quantity_type: 'fixed',
          per_days: null,
          group_id: groupId,
          packlist_id: currentList.value?.id
        }
        
        let response
        if (props.shared) {
          // Retry via public shared link endpoint
          const params = new URLSearchParams({
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          response = await axios.post(`/api/public/${props.link_id}/items?${params}`, itemData)
          
          // Replace optimistic item with real item
          if (sharedPacklist.value?.items) {
            const index = sharedPacklist.value.items.findIndex(i => i.id === item.id)
            if (index !== -1) {
              sharedPacklist.value.items[index] = response.data
              sharedPacklist.value.items.sort((a, b) => a.order_index - b.order_index)
            }
          }
        } else {
          // Retry via regular API
          const result = await listStore.addItem(currentList.value.id, itemData)
          
          if (result.success && listStore.currentList?.items) {
            // Remove optimistic item and let the store handle the real item
            const index = listStore.currentList.items.findIndex(i => i.id === item.id)
            if (index !== -1) {
              listStore.currentList.items.splice(index, 1)
            }
          }
        }
        
        const quantityText = quantity > 1 ? ` (${quantity}x)` : ''
        handleMessage({ message: `"${itemName}${quantityText}" added to ${groupName}!`, color: 'success' })
        
      } catch (error) {
        console.error('Failed to retry item:', error)
        
        // Mark as failed again
        item._isLoading = false
        item._hasError = true
        
        let errorMessage = 'Failed to add item'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to add items'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      }
    }


    const removeOptimisticItem = (item) => {
      if (props.shared && sharedPacklist.value?.items) {
        const index = sharedPacklist.value.items.findIndex(i => i.id === item.id)
        if (index !== -1) {
          sharedPacklist.value.items.splice(index, 1)
        }
      } else if (!props.shared && listStore.currentList?.items) {
        const index = listStore.currentList.items.findIndex(i => i.id === item.id)
        if (index !== -1) {
          listStore.currentList.items.splice(index, 1)
        }
      }
    }

    const openDeleteSelectedDialog = () => {
      if (selectedItems.value.length > 0) {
        showDeleteSelectedDialog.value = true
      }
    }

    const confirmDeleteSelectedItems = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        deletingSelectedItems.value = true
        
        // Create an array of promises for deletion
        const deletionPromises = selectedItems.value.map(item => {
          if (props.shared) {
            // Use shared link API
            const password = authenticatedPassword.value
            const passwordParam = password ? `&password=${encodeURIComponent(password)}` : ''
            
            return axios.delete(
              `/api/public/${props.link_id}/items/${item.id}?hmac=${props.hmac}${passwordParam}`
            )
          } else {
            // Use authenticated API
            return listStore.deleteItem(item.id)
          }
        })
        
        await Promise.all(deletionPromises)
        
        // Refresh data
        if (props.shared) {
          await loadSharedData(authenticatedPassword.value)
        } else {
          await listStore.fetchList(props.listId)
        }
        
        showDeleteSelectedDialog.value = false
        snackbarText.value = `Successfully deleted ${selectedItems.value.length} items`
        snackbarColor.value = 'success'
        snackbar.value = true
        
      } catch (error) {
        console.error('Error deleting selected items:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to delete selected items'
        snackbarText.value = errorMessage
        snackbarColor.value = 'error'
        snackbar.value = true
      } finally {
        deletingSelectedItems.value = false
      }
    }

    const confirmUnselectSelectedItems = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        unselectingSelectedItems.value = true
        
        // Create an array of promises for check updates
        const unselectPromises = selectedItems.value.map(item => {
          if (props.shared) {
            // Update via consolidated endpoint with shared link parameters
            const params = new URLSearchParams({
              link_id: props.link_id,
              hmac: props.hmac
            })
            if (authenticatedPassword.value) {
              params.append('password', authenticatedPassword.value)
            }
            
            return axios.put(`/api/items/${item.id}?${params}`, { is_checked: false })
          } else {
            // Update via regular API
            return listStore.updateItem(item.id, { is_checked: false })
          }
        })
        
        await Promise.all(unselectPromises)
        
        // Refresh data
        if (props.shared) {
          await loadSharedData(authenticatedPassword.value)
        } else {
          // Optimistically update local state to avoid full reload flicker
          selectedItems.value.forEach(item => {
            item.is_checked = false
          })
          
          await listStore.fetchList(props.listId)
        }
        
        showUnselectSelectedDialog.value = false
        snackbarText.value = `Successfully unselected ${selectedItems.value.length} items`
        snackbarColor.value = 'success'
        snackbar.value = true
        
      } catch (error) {
        console.error('Error unselecting items:', error)
        const errorMessage = error.response?.data?.detail || 'Failed to unselect items'
        snackbarText.value = errorMessage
        snackbarColor.value = 'error'
        snackbar.value = true
      } finally {
        unselectingSelectedItems.value = false
      }
    }

    const openEditPacklistDialog = () => {
      showEditPacklistDialog.value = true
    }

    const handlePacklistUpdated = async () => {
      // Refresh the current list to show updated data
      if (!props.shared && listStore.currentList) {
        await listStore.setCurrentList(listStore.currentList)
      }
    }

    const handleGroupUpdated = async () => {
      // Refresh the current list to show updated group data
      if (!props.shared && listStore.currentList) {
        await listStore.setCurrentList(listStore.currentList)
        await groupStore.fetchGroups(listStore.currentList.id)
      }
    }

    const handleGroupDeleted = async () => {
      // Refresh the current list to show updated group data
      if (!props.shared && listStore.currentList) {
        await listStore.setCurrentList(listStore.currentList)
        await groupStore.fetchGroups(listStore.currentList.id)
      }
    }

    const handleItemUpdated = async (updatedItem) => {
      // Refresh the current list to show updated item data
      if (props.shared) {
        // For shared links, update the item locally instead of reloading all data
        if (updatedItem && sharedPacklist.value?.items) {
          const itemIndex = sharedPacklist.value.items.findIndex(item => item.id === updatedItem.id)
          if (itemIndex !== -1) {
            // Update the item in place
            sharedPacklist.value.items[itemIndex] = { ...sharedPacklist.value.items[itemIndex], ...updatedItem }
            
            // If the item's group has changed, update the groups data
            if (updatedItem.group && !sharedGroups.value.find(g => g.id === updatedItem.group.id)) {
              sharedGroups.value.push(updatedItem.group)
            }
          }
        } else {
          // Fallback to full reload if we don't have the updated item data
          await loadSharedData(authenticatedPassword.value)
        }
      } else if (listStore.currentList) {
        await listStore.setCurrentList(listStore.currentList)
        await groupStore.fetchGroups(listStore.currentList.id)
      }
    }

    const handleMessage = ({ message, color }) => {
      snackbarText.value = message
      snackbarColor.value = color
      snackbar.value = true
    }

    const onItemReorder = async () => {
      if (!currentList.value) return
      if (props.shared && !canReorderItems.value) return
      
      const allItems = []
      groupedItems.value.forEach(group => {
        allItems.push(...group.items)
      })
      
      const itemIds = allItems.map(item => item.id)
      
      try {
        if (props.shared) {
          // Reorder via consolidated endpoint with shared link parameters
          const params = new URLSearchParams({
            link_id: props.link_id,
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          await axios.put(`/api/packlists/${currentList.value.id}/reorder?${params}`, itemIds)
          
          // Update local shared data to reflect the new order
          if (sharedPacklist.value?.items) {
            // Create a mapping of item ID to the new order index
            const orderMap = new Map()
            itemIds.forEach((itemId, index) => {
              orderMap.set(itemId, index)
            })
            
            // Update the order_index for each item in the local data
            sharedPacklist.value.items.forEach(item => {
              if (orderMap.has(item.id)) {
                item.order_index = orderMap.get(item.id)
              }
            })
            
            // Sort the items by the new order_index to ensure correct display
            sharedPacklist.value.items.sort((a, b) => a.order_index - b.order_index)
          }
        } else {
          // Reorder via regular API
          await listStore.reorderItems(currentList.value.id, itemIds)
        }
      } catch (error) {
        console.error('Failed to reorder items:', error)
        
        let errorMessage = 'Failed to reorder items'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to reorder items'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
        
        // Reload the data to revert any local changes
        if (props.shared) {
          await loadSharedData(authenticatedPassword.value)
        }
      }
    }

    const updateItemCheck = async (item, event) => {
      const isChecked = event?.target?.checked ?? !item.is_checked
      item.is_checked = isChecked
      
      try {
        if (props.shared) {
          // Update via consolidated endpoint with shared link parameters
          const params = new URLSearchParams({
            link_id: props.link_id,
            hmac: props.hmac
          })
          if (authenticatedPassword.value) {
            params.append('password', authenticatedPassword.value)
          }
          
          await axios.put(`/api/items/${item.id}?${params}`, { is_checked: isChecked })
        } else {
          // Update via regular API
          await listStore.updateItem(item.id, { is_checked: isChecked })
        }
      } catch (error) {
        // Revert the checkbox state on error
        item.is_checked = !isChecked
        event.target.checked = !isChecked
        console.error('Failed to update item:', error)
        
        let errorMessage = 'Failed to update item'
        if (error.response?.status === 401) {
          errorMessage = 'Session expired. Please refresh the page.'
        } else if (error.response?.status === 403) {
          errorMessage = 'You do not have permission to update items'
        }
        
        handleMessage({ message: errorMessage, color: 'error' })
      }
    }

    // Floating dock event handlers
    const openAddItemDialog = () => {
      editingItemForAdd.value = null
      showAddItemDialog.value = true
    }

    const openAddGroupDialog = () => {
      showAddGroupDialog.value = true
    }

    const togglePrintSettings = () => {
      controlsExpanded.value = !controlsExpanded.value
    }

    const handleItemSaved = async () => {
      // Refresh data after item is saved
      if (props.shared) {
        // For shared links, reload the shared data
        await loadSharedData(authenticatedPassword.value)
      } else {
        // For authenticated users, refresh list and groups
        if (listStore.currentList) {
          await listStore.setCurrentList(listStore.currentList)
          await groupStore.fetchGroups(listStore.currentList.id)
        }
      }
    }

    const handleGroupCreated = async () => {
      // Refresh data after group is created
      if (!props.shared && listStore.currentList) {
        await listStore.setCurrentList(listStore.currentList)
        await groupStore.fetchGroups(listStore.currentList.id)
      }
    }

    // Function to load list data
    const loadPacklistData = async (listId) => {
      if (!listId) return

      try {
        // Always fetch the latest list data to ensure we have the header image
        const result = await listStore.fetchList(listId)
        if (result.success) {
          listStore.setCurrentList(result.list)
          // Fetch groups for this specific list
          await groupStore.fetchGroups(listId)
        } else {
          // Fallback to existing data if fetch fails
          const list = listStore.lists.find(p => p.id.toString() === listId.toString())
          if (list) {
            listStore.setCurrentList(list)
            await groupStore.fetchGroups(listId)
          }
        }
      } catch (error) {
        console.error('Failed to load list data:', error)
      }
    }

    // Watch for route parameter changes
    watch(
      () => route.params.uuid,
      async (newPacklistId, oldPacklistId) => {
        if (!props.shared && newPacklistId && newPacklistId !== oldPacklistId) {
          await loadPacklistData(newPacklistId)
        }
      }
    )

    onMounted(async () => {
      if (props.shared) {
        // Load shared data
        await loadSharedData()
        // Groups are included in the shared list data, no need to fetch separately
      } else {
        // Load regular authenticated data
        authStore.initializeAuth()
        await listStore.fetchLists()
        
        // Set current list from prop or URL parameter
        const listId = props.listId || route.params.uuid
        if (listId) {
          await loadPacklistData(listId)
        }
      }
    })

    // Clear shared link name when component unmounts
    onUnmounted(() => {
      if (props.shared) {
        listStore.clearSharedLinkName()
      }
    })

    // Watch for print trigger from App.vue (for shared views)
    watch(() => listStore.triggerPrint, (shouldPrint) => {
      if (shouldPrint && props.shared) {
        // Trigger PDF export with current print settings
        exportToPDF()
        // Reset the trigger
        listStore.resetPrintTrigger()
      }
    })

    return {
      // Stores
      listStore,
      groupStore,
      
      // Print settings from composable
      ...printSettings,
      
      // Computed values
      groupedItems,
      totalItems,
      checkedItems,
      groupOptions,
      groupOptionsForAddDialog,
      headerImageStyle,
      headerImageBackgroundStyle,
      headerImageContainerStyle,
      currentList,
      
      // Functions
      getHeaderImageUrl,
      getCalculatedQuantity,
      getGroupData,
      getGroupImageUrl,
      getGroupImageStyle,
      formatDuration,
      getPacklistDisplayName,
      formatDate,
      
      // PDF functions from composable
      exportToPDF,
      downloadPDF,
      printPDF,
      closePDFPreview,
      handlePDFError,
      showPDFPreview,
      previewPDFUrl,
      
      // State
      loading,
      showPasswordDialog,
      passwordError,
      verifyingPassword,
      authenticatedPassword,
      sharedPacklists,
      selectedPacklistIndex,
      showPacklistSelector,
      
      // Dialog state
      showEditPacklistDialog,
      openEditPacklistDialog,
      handlePacklistUpdated,
      showEditGroupDialog,
      selectedGroup,
      showDeleteGroupDialog,
      groupToDelete,
      showEditItemDialog,
      selectedItem,
      showDeleteItemDialog,
      itemToDelete,
      deletingItem,
      showAddItemDialog,
      editingItemForAdd,
      showAddGroupDialog,
      
      // Snackbar
      snackbar,
      snackbarText,
      snackbarColor,
      
      
      // Event handlers
      verifyPasswordAndLoad,
      setCurrentSharedPacklist,
      onPacklistSelected,
      openPacklistSelector,
      editGroup,
      deleteGroup,
      moveGroupUp,
      moveGroupDown,
      editItem,
      confirmDeleteItem,
      cancelDeleteItem,
      deleteItem,
      showDeleteSelectedDialog,
      deletingSelectedItems,
      selectedItems,
      openDeleteSelectedDialog,
      confirmDeleteSelectedItems,
      quickAddItems,
      quickAddLoading,
      quickAddItem,
      quickAddItemAndStay,
      retryOptimisticItem,
      removeOptimisticItem,
      handleGroupUpdated,
      handleGroupDeleted,
      handleItemUpdated,
      handleMessage,
      onItemReorder,
      updateItemCheck,
      
      // Floating dock
      canAddGroups,
      canAddItems,
      canEditGroups,
      canDeleteGroups,
      canEditItems,
      canDeleteItems,
      canReorderItems,
      canReorderGroups,

      openAddGroupDialog,
      togglePrintSettings,
      handleItemSaved,
      handleGroupCreated,
      
      // Shared breadcrumb
      sharedBreadcrumbItems,
      
      // Props
      shared: computed(() => props.shared)
    }
  }
}
</script>

<style scoped>
.print-preview-app {
  background: #f5f5f5 !important;
  height: 100vh;
  overflow: hidden;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  text-align: center;
  background: #f5f5f5;
}

.print-preview-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
}


/* Preview Area */
.preview-area {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: #f5f5f5;
  position: relative;
}


/* Paper Container */
.paper-container {
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  margin: 20px;
  position: relative;
  z-index: 10;
}

/* Paper Container with header image */
.paper-container[style*="background-image"] {
  background-color: transparent;
}

.paper-container[style*="background-image"] .paper-page {
  background: rgba(255, 255, 255, calc(1 - var(--header-image-opacity, 0.5) * 0.8));
  backdrop-filter: blur(1px);
  position: relative;
}

.paper-container[style*="background-image"] .paper-page::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, calc(0.3 - var(--header-image-opacity, 0.5) * 0.2));
  pointer-events: none;
  z-index: 0;
}

.paper-container[style*="background-image"] .paper-page > * {
  position: relative;
  z-index: 1;
}

/* Paper Format Sizes */
.paper-a4 {
  width: 210mm;
  min-height: 297mm;
}

.paper-letter {
  width: 8.5in;
  min-height: 11in;
}

.paper-legal {
  width: 8.5in;
  min-height: 14in;
}

.paper-page {
  width: 100%;
  height: 100%;
  padding: 20mm;
  box-sizing: border-box;
  background: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 12pt;
  line-height: 1.4;
  color: #333;
}

/* Print Header */
.print-header {
  text-align: center;
  margin-bottom: 1rem;
  border-bottom: 2px solid #333;
  padding-bottom: 0.5rem;
}

.list-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 0.25rem;
  color: #333;
}

.list-description {
  font-size: 1rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.print-meta {
  font-size: 0.9rem;
  color: #666;
}

.separator {
  margin: 0 0.5rem;
}

/* Header Image Print Compatibility */
.header-image-print {
  display: none; /* Hidden on screen */
}

.print-content {
  column-gap: 2rem;
  column-fill: auto;
}

.columns-1 { columns: 1; }
.columns-2 { columns: 2; }
.columns-3 { columns: 3; }
.columns-4 { columns: 4; }

.group-section {
  margin-bottom: 1rem;
}


.items-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Default compact sizing */
/* 4-column adjustments for screen view only */
@media screen {
  .columns-4 .print-content {
    column-gap: 0.75rem;
  }

  .columns-4 .group-section {
    margin-bottom: 0.3rem;
  }

  .columns-4 .group-title {
    font-size: 1rem;
    margin-bottom: 0.25rem;
    padding-bottom: 0.125rem;
  }

  .columns-4 .item-row {
    margin-bottom: 0.15rem;
    padding: 0.025rem 0;
  }

  .columns-4 .item-name {
    font-size: 0.8rem;
    line-height: 1.2;
  }

  .columns-4 .item-description {
    font-size: 0.7rem;
    margin-top: 0.05rem;
    line-height: 1.0;
  }

  .columns-4 .checkbox-container {
    margin-right: 0.3rem;
    margin-top: 0.05rem;
  }

  .columns-4 .item-checkbox {
    width: 0.8rem;
    height: 0.8rem;
  }
}

/* Default compact styles */
.print-header {
  margin-bottom: 0.75rem;
  padding-bottom: 0.375rem;
}

.list-title {
  font-size: 1.4rem;
  margin-bottom: 0.15rem;
}

.list-description {
  font-size: 0.8rem;
  margin-bottom: 0.3rem;
}

.print-meta {
  font-size: 0.8rem;
}

.group-section {
  margin-bottom: 0.75rem;
}

.group-title {
  font-size: 1.1rem;
  margin-bottom: 0.375rem;
  padding-bottom: 0.2rem;
}

.item-row {
  margin-bottom: 0.25rem;
  padding: 0.05rem 0;
}

.item-name {
  font-size: 0.85rem;
  line-height: 1.3;
}

.item-description {
  font-size: 0.75rem;
  margin-top: 0.1rem;
  line-height: 1.1;
}

.checkbox-container {
  margin-right: 0.4rem;
  margin-top: 0.1rem;
}

.item-checkbox {
  width: 0.9rem;
  height: 0.9rem;
}

/* Print styles */
@media print {
  /* Hide browser print metadata (Firefox headers/footers) */
  @page {
    margin: 0.5in;
    size: auto;
    /* Remove default headers and footers */
    @top-left { content: ""; }
    @top-center { content: ""; }
    @top-right { content: ""; }
    @bottom-left { content: ""; }
    @bottom-center { content: ""; }
    @bottom-right { content: ""; }
  }
  
  .no-print {
    display: none !important;
  }
  
  /* Print layout adjustments */
  .print-preview-app {
    background: white !important;
    height: auto !important;
    overflow: visible !important;
  }
  
  .print-preview-container {
    height: auto !important;
    display: block !important;
  }
  
  .preview-area {
    padding: 0 !important;
    background: white !important;
    overflow: visible !important;
    display: block !important;
  }
  
  .paper-container {
    width: 100% !important;
    height: auto !important;
    margin: 0 !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    background: white !important;
  }
  
  .paper-page {
    width: 100% !important;
    height: auto !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  
  /* Header Image Print Compatibility */
  .header-image-background {
    display: none !important; /* Hide background version in print */
  }
  
  .header-image-print {
    display: block !important; /* Show img version in print */
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    max-width: none !important;
    height: auto !important;
    min-height: 200px !important;
    z-index: -1 !important;
    object-fit: cover !important;
    object-position: center top !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  .print-header,
  .print-content {
    position: relative !important;
    z-index: 1 !important;
    background: rgba(255, 255, 255, 0.8) !important;
  }
  
  /* Default compact size for print */
  .print-view {
    font-size: 10pt;
    line-height: 1.3;
  }
  
  /* Compact header elements */
  .list-title {
    font-size: 12pt !important;
    margin-bottom: 0.1rem !important;
  }
  
  .list-description {
    font-size: 9pt !important;
    margin-bottom: 0.2rem !important;
  }
  
  .print-meta {
    font-size: 9pt !important;
  }
  
  .group-title {
    font-size: 12pt;
    margin-bottom: 0.3rem;
  }
  
  .group-image-background {
    aspect-ratio: 24/5;
    /* Ensure images print in black and white if needed */
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  
  .group-header-container.with-image {
    margin-bottom: 0.6rem;
  }
  
  .item-name {
    font-size: 9pt;
  }
  
  .item-description {
    font-size: 8pt;
  }
  
  .group-section {
    margin-bottom: 0.6rem;
  }
  
  .item-row {
    margin-bottom: 0.2rem;
  }
  
  /* Prevent page breaks between header and content - Chrome specific fixes */
  .print-view {
    page-break-inside: avoid;
    break-inside: avoid;
    /* Chrome-specific: Force content to stay together */
    display: block !important;
    overflow: visible !important;
  }
  
  .print-container {
    /* Chrome fix: Force header and content to stay together */
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    display: block !important;
    /* Treat as single unit */
    orphans: 3;
    widows: 3;
  }
  
  .print-header {
    page-break-after: avoid !important;
    break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    /* Chrome-specific: Reduce margins and force inline-block */
    margin-bottom: 0.3rem !important;
    padding-bottom: 0.2rem !important;
    display: block !important;
    /* Chrome fix: Prevent orphaning */
    orphans: 3;
    widows: 3;
  }
  
  .print-content {
    page-break-before: avoid !important;
    break-before: avoid !important;
    margin-top: 0 !important;
    /* Chrome-specific: Force content to follow header */
    display: block !important;
    /* Chrome fix: Prevent orphaning */
    orphans: 3;
    widows: 3;
  }
  
  /* Chrome-specific: Force header and content to be treated as one unit */
  .print-header + .print-content {
    margin-top: 0 !important;
    padding-top: 0 !important;
  }
  
  /* Keep header and first group together */
  .print-header + .print-content .group-section:first-child {
    page-break-before: avoid !important;
    break-before: avoid !important;
  }
  
  /* Additional fix for 4-column layouts and when images are disabled */
  .columns-4 .print-header,
  .images-disabled .print-header {
    page-break-after: avoid !important;
    break-after: avoid !important;
    margin-bottom: 0.5rem !important;
    padding-bottom: 0.2rem !important;
  }
  
  .columns-4.print-content,
  .images-disabled .print-content {
    page-break-before: avoid !important;
    break-before: avoid !important;
    margin-top: 0 !important;
  }
  
  /* Adjust header sizing for print to prevent page breaks */
  .print-header {
    margin-bottom: 0.4rem !important;
    padding-bottom: 0.2rem !important;
  }
  
  /* Comprehensive 4-column print fixes */
  .columns-4 {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  
  .columns-4 .print-header {
    page-break-after: avoid !important;
    break-after: avoid !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    margin-bottom: 0.2rem !important;
    padding-bottom: 0.1rem !important;
  }
  
  /* 4-column specific sizing */
  .columns-4 .list-title {
    font-size: 10pt !important;
    margin-bottom: 0.05rem !important;
  }
  
  .columns-4 .list-description {
    font-size: 8pt !important;
    margin-bottom: 0.1rem !important;
  }
  
  .columns-4 .print-meta {
    font-size: 7pt !important;
  }
  
  .columns-4 .print-content {
    page-break-before: avoid !important;
    break-before: avoid !important;
    page-break-inside: auto;
    break-inside: auto;
    column-fill: balance;
    column-gap: 0.5rem;
    margin-top: 0 !important;
  }
  
  .columns-4 .group-section {
    margin-bottom: 0.2rem;
    break-inside: auto;
    page-break-inside: auto;
  }
  
  .columns-4 .group-title {
    font-size: 10pt !important;
    margin-bottom: 0.15rem !important;
    padding-bottom: 0.1rem !important;
  }
  
  .columns-4 .item-row {
    margin-bottom: 0.1rem !important;
    padding: 0.02rem 0 !important;
    break-inside: avoid;
    page-break-inside: avoid;
  }
  
  .columns-4 .item-name {
    font-size: 8pt !important;
    line-height: 1.1 !important;
  }
  
  .columns-4 .item-description {
    font-size: 7pt !important;
    margin-top: 0.05rem !important;
    line-height: 1.0 !important;
  }
  
  .columns-4 .checkbox-container {
    margin-right: 0.25rem !important;
    margin-top: 0.02rem !important;
  }
  
  .columns-4 .item-checkbox {
    width: 0.7rem !important;
    height: 0.7rem !important;
  }
  
  .columns-4 .print-view {
    page-break-before: avoid;
    break-before: avoid;
    font-size: 8pt !important;
    line-height: 1.1 !important;
  }
  
  /* Allow groups to flow across columns */
  .group-section {
    margin-bottom: 0.8rem;
  }
  
  /* Keep group headers with at least one item */
  .group-header-container {
    break-after: avoid;
    page-break-after: avoid;
  }
}

/* Responsive design for screen */
@media screen and (max-width: 1200px) {
  .paper-a4 {
    width: 180mm;
  }
  
  .paper-letter,
  .paper-legal {
    width: 7in;
  }
}

@media screen and (max-width: 768px) {
  .columns-3,
  .columns-4 {
    columns: 2;
  }
  
  .paper-a4 {
    width: 150mm;
  }
  
  .paper-letter,
  .paper-legal {
    width: 6in;
  }
  
  .preview-area {
    padding: 40px 10px 10px 10px; /* Increase top padding for mobile */
  }
}

@media screen and (max-width: 480px) {
  .columns-2,
  .columns-3,
  .columns-4 {
    columns: 1;
  }
  
  .paper-a4 {
    width: 120mm;
  }
  
  .paper-letter,
  .paper-legal {
    width: 5in;
  }
  
  .paper-page {
    padding: 15mm;
  }
}

/* Multi-list indicator */
.multi-list-indicator {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 999;
  cursor: pointer;
  transition: all 0.2s ease;
}

.multi-list-indicator:hover {
  transform: scale(1.05);
}


/* Shared breadcrumb styling */
.shared-breadcrumb {
  background: rgba(0, 0, 0, 0.02);
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  margin-bottom: 4px;
}

.shared-breadcrumb .v-breadcrumbs {
  padding: 8px 0;
}

.shared-breadcrumb .v-breadcrumbs-item {
  font-size: 0.875rem;
}

.cursor-pointer {
  cursor: pointer;
}

/* Make the dropdown arrow more subtle */
.shared-breadcrumb .v-breadcrumbs-item .v-icon {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.shared-breadcrumb .v-breadcrumbs-item:hover .v-icon {
  opacity: 1;
}

/* Mobile responsiveness for breadcrumb */
@media (max-width: 768px) {
  .shared-breadcrumb .v-breadcrumbs-item {
    font-size: 0.75rem;
  }
  
  /* Truncate long list names on mobile */
  .shared-breadcrumb .v-breadcrumbs-item {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

@media (max-width: 480px) {
  .shared-breadcrumb .v-breadcrumbs-item {
    font-size: 0.7rem;
    max-width: 150px;
  }
}

@media print {
  .multi-list-indicator {
    display: none !important;
  }
  
  .shared-breadcrumb {
    display: none !important;
  }
}

</style>